/* eslint-disable no-undef */
/**
 * Orbital Features — scroll-triggered assembly animation
 * Items slide inward from their spread positions, image scales up, halo expands.
 * Desktop only; skips on mobile and when prefers-reduced-motion is set.
 */
(function () {
  if (customElements.get('one-client-orbital')) return;

  var MOBILE = 750;
  var pluginsReady = false;

  function ensurePlugins() {
    if (pluginsReady) return true;
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined')
      return false;
    gsap.registerPlugin(ScrollTrigger);
    pluginsReady = true;
    return true;
  }

  class OrbitalFeatures extends HTMLElement {
    connectedCallback() {
      if (this.dataset.animate !== 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this._resizeHandler = this._onResize.bind(this);
      window.addEventListener('resize', this._resizeHandler);
      this._waitForGsap(this._init.bind(this));
    }

    disconnectedCallback() {
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
      }
      this._destroy();
    }

    _waitForGsap(cb) {
      if (ensurePlugins()) {
        cb();
        return;
      }
      var self = this;
      var attempts = 0;
      var poll = setInterval(function () {
        if (ensurePlugins() || ++attempts > 100) {
          clearInterval(poll);
          if (pluginsReady) cb.call(self);
        }
      }, 50);
    }

    _onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        if (window.innerWidth < MOBILE) {
          this._destroy();
        } else if (!this._active) {
          this._waitForGsap(this._init.bind(this));
        }
      }, 200);
    }

    _init() {
      if (window.innerWidth < MOBILE) return;
      if (!ensurePlugins()) return;

      this._active = true;

      var items = this.querySelectorAll('[data-orbital-item]');
      var halo = this.querySelector(
        '[data-orbital-image] [aria-hidden="true"]',
      );
      var productImg = this.querySelector('[data-orbital-product-image]');

      if (!items.length) return;

      var left = [];
      var right = [];
      items.forEach(function (item, i) {
        if (i < 3) left.push(item);
        else right.push(item);
      });

      // Set initial states — items spread outward, faded
      gsap.set(left, { x: -60, opacity: 0 });
      gsap.set(right, { x: 60, opacity: 0 });
      if (productImg) gsap.set(productImg, { scale: 0.85, opacity: 0 });
      if (halo) gsap.set(halo, { scale: 0.6, opacity: 0 });

      // Assembly timeline — plays once when section scrolls into view
      var tl = gsap.timeline({ paused: true });

      this._scrollTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top 80%',
        once: true,
        onEnter: function () {
          tl.play();
        },
      });

      // Halo expands first
      if (halo) {
        tl.to(
          halo,
          { scale: 1, opacity: 1, duration: 0.4, ease: 'power2.out' },
          0,
        );
      }

      // Image scales in
      if (productImg) {
        tl.to(
          productImg,
          { scale: 1, opacity: 1, duration: 0.5, ease: 'power2.out' },
          0.1,
        );
      }

      // Left items slide in with stagger (top first)
      tl.to(
        left,
        {
          x: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.08,
          ease: 'power2.out',
        },
        0.15,
      );

      // Right items slide in with stagger (top first)
      tl.to(
        right,
        {
          x: 0,
          opacity: 1,
          duration: 0.4,
          stagger: 0.08,
          ease: 'power2.out',
        },
        0.15,
      );

      this._timeline = tl;
    }

    _destroy() {
      if (!this._active) return;
      this._scrollTrigger?.kill(true);
      this._timeline?.kill();
      this._timeline = null;
      this._scrollTrigger = null;
      this._active = false;

      // Clear inline styles
      var items = this.querySelectorAll('[data-orbital-item]');
      var halo = this.querySelector(
        '[data-orbital-image] [aria-hidden="true"]',
      );
      var productImg = this.querySelector('[data-orbital-product-image]');
      var all = Array.from(items);
      if (halo) all.push(halo);
      if (productImg) all.push(productImg);

      all.forEach(function (el) {
        gsap.set(el, { clearProps: 'all' });
      });
    }
  }

  customElements.define('one-client-orbital', OrbitalFeatures);
})();
